import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const ws = () => supabase.schema('workspace');

/**
 * 멤버·초대 데이터 로드 및 수정 훅.
 * - members: workspace.members (org_id 기준)
 * - invitations: status='pending' 만 (대기 중 초대)
 * 권한 변경/취소는 RLS 가 admin 만 허용한다.
 */
export default function useMembers() {
  const { membership } = useAuth();
  const orgId = membership?.org_id ?? null;

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMembers = useCallback(async () => {
    if (!orgId) return;
    const { data, error: err } = await ws()
      .from('members')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: true });
    if (err) { setError(err.message); return; }
    setMembers(data || []);
  }, [orgId]);

  const loadInvites = useCallback(async () => {
    if (!orgId) return;
    const { data, error: err } = await ws()
      .from('invitations')
      .select('*')
      .eq('org_id', orgId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (err) { setError(err.message); return; }
    setInvites(data || []);
  }, [orgId]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([loadMembers(), loadInvites()]);
    setLoading(false);
  }, [loadMembers, loadInvites]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!orgId) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      await Promise.all([loadMembers(), loadInvites()]);
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [orgId, loadMembers, loadInvites]);

  /** 멤버 역할 변경. RLS 가 admin 만 허용. */
  const updateRole = useCallback(async (memberId, newRole) => {
    const prev = members;
    setMembers((cur) => cur.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)));
    const { error: err } = await ws()
      .from('members')
      .update({ role: newRole })
      .eq('id', memberId);
    if (err) {
      setMembers(prev); // 롤백
      return { ok: false, error: err.message };
    }
    return { ok: true };
  }, [members]);

  /** 초대 생성. RPC create_invitation 으로만. 반환된 token 으로 링크 구성. */
  const createInvite = useCallback(async ({ email, role = 'member', teamspace = null }) => {
    const { data, error: err } = await supabase.rpc('create_invitation', {
      p_email: (email || '').trim(),
      p_role: role,
      p_teamspace: teamspace,
    });
    if (err) return { ok: false, error: err.message };
    if (!data?.ok) return { ok: false, error: data?.error || '초대 생성 실패' };
    await loadInvites();
    return { ok: true, token: data.token, email: data.email };
  }, [loadInvites]);

  /** 대기 중 초대 취소 (status='revoked'). RLS 가 admin 만 허용. */
  const revokeInvite = useCallback(async (inviteId) => {
    const prev = invites;
    setInvites((cur) => cur.filter((i) => i.id !== inviteId));
    const { error: err } = await ws()
      .from('invitations')
      .update({ status: 'revoked' })
      .eq('id', inviteId);
    if (err) {
      setInvites(prev); // 롤백
      return { ok: false, error: err.message };
    }
    return { ok: true };
  }, [invites]);

  return {
    orgId,
    members,
    invites,
    loading,
    error,
    reload,
    updateRole,
    createInvite,
    revokeInvite,
  };
}
